import React from 'react';
import Error from 'next/error';
import {
    logger,
    Utils,
    Store,
    StoreDeliveryThreshold,
    Translations,
    TranslationPages,
    Pages,
} from '@deporvillage/frontend-common';
import Translation from '@data-service/controller/translationController';
import Configuration from '@data-service/controller/configurationController';
import ConfigTemplate from '@templates/Config';
import PageTemplate from '@templates/Page';
import HomeTemplate from '@templates/Home';
import Home from '@components/home/Home';
import getContext from '@utils/context';
import client from '@client/client';
import { Home as HomeQuery } from '@query/home';
import { Layout as LayoutQuery } from '@query/layout';
import HttpHeaderNames from '@utils/headers';

logger.setLevel('WARN');

const HomePage = ({
    model, i18n, config, layout, err,
}) => (
    err ? (
        <Error statusCode={err.statusCode} />
    ) : (
        <ConfigTemplate i18n={i18n} config={config}>
            <PageTemplate model={{ layout }}>
                <HomeTemplate>
                    <Home model={model} />
                </HomeTemplate>
            </PageTemplate>
        </ConfigTemplate>
    )
);

export async function getServerSideProps(context) {
    const {
        session,
        store,
        lang,
        locale,
        country,
        url,
        geoIp,
    } = await getContext(context);

    if (!Store.isValidLang(lang)) {
        context.res.statusCode = 400;
        return { props: { err: { statusCode: 400, message: 'Invalid language' } } };
    }

    const [
        model,
        config,
        i18n,
        i18nGeoIp,
        layout,
    ] = await Promise.all([
        client.query(HomeQuery, {
            mosaicInput: {
                limit: 8,
                category: 2,
                isMobile: false,
            },
        }, {
            [HttpHeaderNames.Store]: store,
            [HttpHeaderNames.Country]: country.toLowerCase(),
            [HttpHeaderNames.Language]: lang,
            [HttpHeaderNames.RequestId]: session?.requestId,
        }),
        Configuration.getAll({
            session,
            store,
            lang,
            country,
            locale,
        }),
        Translation.get({
            session,
            locale,
            keys: Translations.getKeys(TranslationPages.Home),
        }),
        geoIp && geoIp.locale ?
            Translation.get({
                session,
                locale: geoIp.locale,
                keys: 'geo_ip',
            }) : {},
        client.query(LayoutQuery, {}, {
            [HttpHeaderNames.PlatformStoreUuid]: Store.getStoreUuid(locale),
            [HttpHeaderNames.RequestId]: session?.requestId,
        }),
    ]);

    const {
        CategoryMenu,
        GetPageConfigFlags,
        GetPageAcceptanceMarks,
        GetStores,
        PageHomeTopBrands,
        ListCategoryBanners,
        PageHomeAdManagerBanners,
    } = {
        ...model?.data || {},
        ...layout?.data || {},
    };

    return {
        props: {
            model: {
                topBrands: PageHomeTopBrands,
                mosaic: ListCategoryBanners,
            },
            config: {
                ...config,
                geoIp,
                lang,
                locale,
                storeId: store,
                page: Pages.Home,
                url,
                origin: `https:${url}`,
                environment: process.env.ENVIRONMENT || 'dev',
                freeDeliveryThreshold: StoreDeliveryThreshold[locale],
                extension: Store.getExtension(locale),
                ad: PageHomeAdManagerBanners,
            },
            i18n: {
                ...i18n,
                ...i18nGeoIp,
            },
            layout: {
                nav: Utils.getMenu(CategoryMenu),
                stores: GetStores,
                icons: GetPageAcceptanceMarks,
                ...GetPageConfigFlags,
            },
        },
    };
}

export default HomePage;
